import { useState, useEffect, useCallback, useMemo } from "react";
import Title from "../shared/components/Title";
import apiClient from "../utils/apiClient";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell,
} from "recharts";

/* ─── brand palette ───────────────────────────────────────── */
const CORAL = "#F4736E";
const BLUE  = "#1a5691";
const NAVY  = "#144677";

/* ─── constants ───────────────────────────────────────────── */
function fyStart() {
  const t = new Date();
  const y = t.getMonth() >= 6 ? t.getFullYear() : t.getFullYear() - 1;
  return `${y}-07-01`;
}
const todayIso  = new Date().toISOString().slice(0, 10);
const TARGET    = 95;
const C_RAW     = CORAL;
const C_CARRIER = BLUE;
const C_TOOL    = NAVY;

/* ─── RAG thresholds ──────────────────────────────────────── */
const RAG_GREEN  = "#22c55e";
const RAG_AMBER  = "#f59e0b";
const RAG_RED    = "#ef4444";
const RAG_TEXT_G = "#16a34a";
const RAG_TEXT_A = "#d97706";
const RAG_TEXT_R = "#dc2626";

/* ─── helpers ─────────────────────────────────────────────── */
function pctFill(v) {
  if (v == null) return "#d1d5db";
  return v >= 90 ? RAG_GREEN : v >= 80 ? RAG_AMBER : RAG_RED;
}
function pctColor(v) {
  if (v == null) return "#9ca3af";
  return v >= 90 ? RAG_TEXT_G : v >= 80 ? RAG_TEXT_A : RAG_TEXT_R;
}
function fmtMoney(v) {
  if (!v && v !== 0) return "—";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}
function fmtCpkg(v) {
  if (v == null) return "—";
  return `$${Number(v).toFixed(2)}/kg`;
}
function gpColor(v) {
  if (v == null) return "#9ca3af";
  return v >= 19 ? RAG_TEXT_G : v >= 8 ? RAG_TEXT_A : RAG_TEXT_R;
}

/* ─── UI atoms ────────────────────────────────────────────── */
function KpiCard({ label, value, sub, dot, pct }) {
  const bc = pct == null ? "#e5e7eb" : pct >= 90 ? RAG_GREEN : pct >= 80 ? RAG_AMBER : RAG_RED;
  const vc = pct == null ? "#6b7280" : pct >= 90 ? RAG_TEXT_G : pct >= 80 ? RAG_TEXT_A : RAG_TEXT_R;
  return (
    <div className="bg-white rounded-2xl shadow-sm px-5 py-4" style={{ borderLeft: `4px solid ${bc}` }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {dot && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: dot }} />}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">{label}</p>
        </div>
        {pct != null && pct < 90 && (
          <span className="text-xs font-bold px-1.5 py-0.5 rounded-md"
            style={{ background: `${bc}18`, color: bc }}>
            {(pct - TARGET).toFixed(1)}%
          </span>
        )}
      </div>
      <p className="text-3xl font-black" style={{ color: vc }}>{value ?? "—"}</p>
      {sub && <p className="text-xs text-gray-400 mt-1.5">{sub}</p>}
    </div>
  );
}

function FinCard({ label, value, sub, alert }) {
  const bg = alert
    ? `linear-gradient(135deg, ${CORAL}, #d45a56)`
    : `linear-gradient(135deg, ${BLUE}, ${NAVY})`;
  return (
    <div className="rounded-2xl shadow-sm px-5 py-4" style={{ background: bg }}>
      <p className="text-xs font-semibold uppercase tracking-widest mb-1"
        style={{ color: "rgba(255,255,255,0.6)" }}>{label}</p>
      <p className="text-2xl font-black text-white">{value ?? "—"}</p>
      {sub && <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.5)" }}>{sub}</p>}
    </div>
  );
}

function MiniBar({ value }) {
  if (value == null) return <span className="text-gray-300 text-xs">—</span>;
  const color = pctFill(value);
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.min(value, 100)}%`, background: color }} />
      </div>
      <span className="text-xs tabular-nums font-bold" style={{ color }}>{value}%</span>
    </div>
  );
}

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl shadow-xl overflow-hidden text-xs min-w-[200px]"
      style={{ border: `1px solid ${NAVY}20` }}>
      <div className="px-4 py-2.5" style={{ background: NAVY }}>
        <p className="font-bold text-white">{label}</p>
      </div>
      <div className="px-4 py-3 space-y-2">
        {payload.map(p => (
          <div key={p.dataKey} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
              <span className="text-gray-500">{p.name}</span>
            </div>
            <span className="font-bold" style={{ color: p.color }}>
              {p.value != null ? `${p.value}%` : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

function ViewTab({ label, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
        active ? "text-white shadow-md" : "bg-white text-gray-500 hover:bg-gray-100 shadow-sm"
      }`}
      style={active ? { background: NAVY } : {}}>
      {label}
    </button>
  );
}

function SectionHeader({ title, sub }) {
  return (
    <div className="mb-5 pl-3" style={{ borderLeft: `3px solid ${CORAL}` }}>
      <h2 className="text-base font-bold text-gray-800">{title}</h2>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-2xl shadow-sm ${className}`}>{children}</div>;
}

function AboutBanner({ items }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl overflow-hidden"
      style={{ border: `1px solid ${NAVY}22`, background: `${BLUE}07` }}>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-white/40 transition-colors">
        <span className="text-xs font-semibold flex items-center gap-2" style={{ color: NAVY }}>
          <span style={{ fontSize: 14 }}>ℹ</span> About this data
        </span>
        <span className="text-xs font-bold" style={{ color: NAVY }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((item, i) => (
            <div key={i} className="bg-white rounded-lg px-3 py-2.5"
              style={{ border: `1px solid ${NAVY}14` }}>
              <p className="text-xs font-bold mb-1" style={{ color: NAVY }}>{item.label}</p>
              <p className="text-xs text-gray-500 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const TH = ({ children, right }) => (
  <th className={`px-5 py-3 text-xs font-semibold text-white uppercase tracking-wider ${right ? "text-right" : "text-left"}`}>
    {children}
  </th>
);

/* ══════════════════════════════════════════════════════════ */
export default function DifotReport() {
  const [view,         setView]         = useState("overview");
  const [fromDate,     setFromDate]     = useState(fyStart());
  const [toDate,       setToDate]       = useState(todayIso);
  const [carrier,      setCarrier]      = useState("");
  const [portfolio,    setPortfolio]    = useState("");
  const [state,        setState]        = useState("");
  const [drillCarrier, setDrillCarrier] = useState(null);

  const [summary,        setSummary]        = useState(null);
  const [trend,          setTrend]          = useState([]);
  const [byCarrier,      setByCarrier]      = useState([]);
  const [drillData,      setDrillData]      = useState([]);
  const [byCustomer,     setByCustomer]     = useState([]);
  const [byState,        setByState]        = useState([]);
  const [failureReasons, setFailureReasons] = useState(null);
  const [filterOpts,     setFilterOpts]     = useState({ carriers: [], portfolios: [], states: [] });

  /* reports tab state */
  const [reportCustomer,  setReportCustomer]  = useState("");
  const [lateAnalysis,    setLateAnalysis]    = useState(null);
  const [laneAnalysis,    setLaneAnalysis]    = useState([]);
  const [reportSummary,   setReportSummary]   = useState(null);
  const [reportByState,   setReportByState]   = useState([]);
  const [loadingReport,   setLoadingReport]   = useState(false);

  /* cross-dimensional state */
  const [stateCarrier,    setStateCarrier]    = useState(null);
  const [loadingMatrix,   setLoadingMatrix]   = useState(false);

  const [loadingTrend,    setLoadingTrend]    = useState(true);
  const [loadingCarrier,  setLoadingCarrier]  = useState(true);
  const [loadingDrill,    setLoadingDrill]    = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [error,           setError]           = useState(null);

  useEffect(() => {
    apiClient.get(`/api/v1/difotReport/filter-options?from_date=${fromDate}&to_date=${toDate}`)
      .then(r => r.json()).then(setFilterOpts).catch(() => {});
  }, [fromDate, toDate]);

  const loadData = useCallback(() => {
    setLoadingTrend(true); setLoadingCarrier(true); setLoadingCustomer(true);
    setError(null);
    const base  = `from_date=${fromDate}&to_date=${toDate}`;
    const withC = carrier   ? `&carrier=${encodeURIComponent(carrier)}`     : "";
    const withP = portfolio ? `&portfolio=${encodeURIComponent(portfolio)}` : "";
    const withS = state     ? `&state=${encodeURIComponent(state)}`         : "";

    Promise.all([
      apiClient.get(`/api/v1/difotReport/summary?${base}${withC}${withS}`).then(r => r.json()),
      apiClient.get(`/api/v1/difotReport/trend?${base}${withC}${withS}`).then(r => r.json()),
      apiClient.get(`/api/v1/difotReport/by-carrier?${base}${withS}`).then(r => r.json()),
      apiClient.get(`/api/v1/difotReport/by-customer?${base}${withP}${withS}`).then(r => r.json()),
      apiClient.get(`/api/v1/difotReport/by-state?${base}${withC}${withS}`).then(r => r.json()),
      apiClient.get(`/api/v1/difotReport/failure-reasons?${base}${withC}${withS}`).then(r => r.json()),
    ])
      .then(([s, t, c, cu, st, fr]) => {
        setSummary(s); setTrend(t); setByCarrier(c); setByCustomer(cu); setByState(st); setFailureReasons(fr);
      })
      .catch(() => setError("Failed to load. Check your VPN and try again."))
      .finally(() => { setLoadingTrend(false); setLoadingCarrier(false); setLoadingCustomer(false); });
  }, [fromDate, toDate, carrier, portfolio, state]);

  useEffect(() => { loadData(); }, [loadData]);

  /* preset date helpers */
  function applyPreset(preset) {
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth();
    if (preset === "mtd") {
      setFromDate(new Date(y, m, 1).toISOString().slice(0, 10));
      setToDate(todayIso);
    } else if (preset === "qtd") {
      const qStart = new Date(y, Math.floor(m / 3) * 3, 1);
      setFromDate(qStart.toISOString().slice(0, 10));
      setToDate(todayIso);
    } else if (preset === "fy") {
      setFromDate(fyStart());
      setToDate(todayIso);
    }
  }

  /* load reports tab data */
  const loadReport = useCallback(() => {
    if (view !== "reports") return;
    setLoadingReport(true);
    const base  = `from_date=${fromDate}&to_date=${toDate}`;
    const withU = reportCustomer ? `&customer=${encodeURIComponent(reportCustomer)}` : "";
    const withC = carrier ? `&carrier=${encodeURIComponent(carrier)}` : "";
    const withS = state   ? `&state=${encodeURIComponent(state)}`     : "";

    Promise.all([
      apiClient.get(`/api/v1/difotReport/summary?${base}${withU}${withC}${withS}`).then(r => r.json()),
      apiClient.get(`/api/v1/difotReport/by-state?${base}${withU}${withC}`).then(r => r.json()),
      apiClient.get(`/api/v1/difotReport/late-analysis?${base}${withU}${withC}${withS}`).then(r => r.json()),
      apiClient.get(`/api/v1/difotReport/lane-analysis?${base}${withU}${withC}${withS}&limit=15`).then(r => r.json()),
    ])
      .then(([s, st, la, ln]) => {
        setReportSummary(s); setReportByState(st); setLateAnalysis(la); setLaneAnalysis(ln);
      })
      .finally(() => setLoadingReport(false));
  }, [view, fromDate, toDate, reportCustomer, carrier, state]);

  useEffect(() => { loadReport(); }, [loadReport]);

  /* load state×carrier matrix when tab is first opened */
  const loadMatrix = useCallback(() => {
    if (view !== "carrier") return;
    setLoadingMatrix(true);
    const base = `from_date=${fromDate}&to_date=${toDate}`;
    apiClient.get(`/api/v1/difotReport/state-carrier?${base}`)
      .then(r => r.json()).then(setStateCarrier)
      .catch(() => {})
      .finally(() => setLoadingMatrix(false));
  }, [view, fromDate, toDate]);

  useEffect(() => { loadMatrix(); }, [loadMatrix]);

  useEffect(() => {
    if (!drillCarrier) { setDrillData([]); return; }
    setLoadingDrill(true);
    const base  = `carrier=${encodeURIComponent(drillCarrier)}&from_date=${fromDate}&to_date=${toDate}`;
    const withS = state ? `&state=${encodeURIComponent(state)}` : "";
    apiClient.get(`/api/v1/difotReport/by-carrier-service?${base}${withS}`)
      .then(r => r.json()).then(setDrillData)
      .catch(() => setDrillData([]))
      .finally(() => setLoadingDrill(false));
  }, [drillCarrier, fromDate, toDate, state]);

  /* derived chart data */
  const trendChart = useMemo(() =>
    [...trend].reverse().map(r => ({ ...r, week: r.week.slice(5) }))
  , [trend]);

  const carrierChart = useMemo(() => byCarrier.slice(0, 15), [byCarrier]);

  const portfolioChart = useMemo(() => {
    const map = {};
    byCustomer.forEach(r => {
      if (!map[r.portfolio]) map[r.portfolio] = { portfolio: r.portfolio, total: 0, raw: 0, carrier_h: 0, tool: 0 };
      map[r.portfolio].total     += r.total;
      map[r.portfolio].raw       += Math.round((r.raw_pct     || 0) / 100 * r.total);
      map[r.portfolio].carrier_h += Math.round((r.carrier_pct || 0) / 100 * r.total);
      map[r.portfolio].tool      += Math.round((r.tool_pct    || 0) / 100 * r.total);
    });
    return Object.values(map).map(p => ({
      portfolio:   p.portfolio,
      total:       p.total,
      raw_pct:     p.total ? +(p.raw       / p.total * 100).toFixed(1) : null,
      carrier_pct: p.total ? +(p.carrier_h / p.total * 100).toFixed(1) : null,
      tool_pct:    p.total ? +(p.tool      / p.total * 100).toFixed(1) : null,
    })).sort((a, b) => b.total - a.total);
  }, [byCustomer]);

  const topCustomerChart = useMemo(() =>
    byCustomer.slice(0, 15).map(r => ({
      ...r, name: r.customer.length > 22 ? r.customer.slice(0, 22) + "…" : r.customer,
    }))
  , [byCustomer]);

  const customerList = useMemo(() =>
    [...new Set(byCustomer.map(r => r.customer))].sort()
  , [byCustomer]);

  /* ─── filter bar ──────────────────────────────────────────── */
  const FilterBar = () => (
    <Card className="p-4 flex flex-wrap gap-4 items-end">
      {[
        {
          label: "From",
          el: <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100" />
        },
        {
          label: "To",
          el: <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100" />
        },
        {
          label: "Carrier",
          el: (
            <select value={carrier} onChange={e => { setCarrier(e.target.value); setDrillCarrier(null); }}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none min-w-[175px]">
              <option value="">All Carriers</option>
              {filterOpts.carriers.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )
        },
        {
          label: "Portfolio",
          el: (
            <select value={portfolio} onChange={e => setPortfolio(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none min-w-[175px]">
              <option value="">All Portfolios</option>
              {filterOpts.portfolios.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )
        },
        {
          label: "State",
          el: (
            <select value={state} onChange={e => setState(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none min-w-[110px]">
              <option value="">All States</option>
              {filterOpts.states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )
        },
      ].map(({ label, el }) => (
        <div key={label}>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{label}</label>
          {el}
        </div>
      ))}
      {(carrier || portfolio || state) && (
        <button onClick={() => { setCarrier(""); setPortfolio(""); setState(""); setDrillCarrier(null); }}
          className="self-end pb-2 text-xs font-semibold hover:underline" style={{ color: CORAL }}>
          Clear all
        </button>
      )}
      {summary && (
        <p className="self-end pb-2 ml-auto text-xs text-gray-400">
          {summary.total?.toLocaleString()} consignments &middot; {summary.period.from} → {summary.period.to}
        </p>
      )}
    </Card>
  );

  /* ─── DIFOT KPI cards ─────────────────────────────────────── */
  const DifotCards = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <KpiCard label="Raw Machship DIFOT"     dot={C_RAW}     pct={summary?.raw?.pct}
        value={summary?.raw?.pct     != null ? `${summary.raw.pct}%`     : "—"}
        sub={`${summary?.raw?.hit?.toLocaleString() ?? "—"} / ${summary?.total?.toLocaleString() ?? "—"} consignments`} />
      <KpiCard label="Carrier Cleansed DIFOT" dot={C_CARRIER} pct={summary?.carrier?.pct}
        value={summary?.carrier?.pct != null ? `${summary.carrier.pct}%` : "—"}
        sub={`${summary?.carrier?.hit?.toLocaleString() ?? "—"} / ${summary?.total?.toLocaleString() ?? "—"} consignments`} />
      <KpiCard label="Tool Cleansed DIFOT"    dot={C_TOOL}    pct={summary?.tool?.pct}
        value={summary?.tool?.pct    != null ? `${summary.tool.pct}%`    : "—"}
        sub={`${summary?.tool?.hit?.toLocaleString() ?? "—"} / ${summary?.total?.toLocaleString() ?? "—"} consignments`} />
      <KpiCard label="POD Availability"       dot="#6366f1"   pct={summary?.pod?.pct}
        value={summary?.pod?.pct     != null ? `${summary.pod.pct}%`     : "—"}
        sub={`${summary?.pod?.hit?.toLocaleString() ?? "—"} / ${summary?.total?.toLocaleString() ?? "—"} consignments`} />
    </div>
  );

  /* ─── financial cards ─────────────────────────────────────── */
  const FinancialCards = () => {
    const f = summary?.financials;
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <FinCard label="Revenue"      value={fmtMoney(f?.revenue)} sub="Total sell price" />
        <FinCard label="Cost"         value={fmtMoney(f?.cost)}    sub="Total cost price" />
        <FinCard label="Gross Profit" value={fmtMoney(f?.gp)}      sub="Revenue minus cost" />
        <FinCard label="GP Margin"    value={f?.gp_margin != null ? `${f.gp_margin}%` : "—"}
          sub="Gross profit margin" alert={f?.gp_margin != null && f.gp_margin < 8} />
        <FinCard label="Cost per KG"  value={fmtCpkg(f?.cost_per_kg)}
          sub={f?.weight_kg != null ? `${(f.weight_kg / 1000).toFixed(0)}t total` : "Total weight"} />
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════
     VIEW 1 — OVERVIEW
  ══════════════════════════════════════════════════════════ */
  const OVERVIEW_ABOUT = [
    {
      label: "Three DIFOT Layers",
      text:  "Raw = original ETA set in Machship at booking. Carrier Cleansed = adjusted where the carrier has agreed a miss wasn't their fault (e.g. customer delay, weather). Tool Cleansed = cora's final official figure after ops team amendments — use this as the primary KPI.",
    },
    {
      label: "Data Source & Refresh",
      text:  "Sourced from FCT_CONSIGNMENTS in Snowflake (DWH_PROD.MART), populated from Machship (TMS). Refreshed daily via the automated dbt pipeline. The date filter defaults to the current financial year (Jul 1 – today).",
    },
    {
      label: "POD Availability",
      text:  "Percentage of consignments with at least one attachment in Machship — used as a proxy for Proof of Delivery lodgement. A low POD % may indicate ops teams are not uploading delivery documents.",
    },
    {
      label: "Financial Metrics",
      text:  "Revenue = total sell price charged to the customer. Cost = cora's internal cost price (COGS). GP = Revenue minus Cost. Margin = GP as a % of Revenue. Cost per KG = Cost ÷ total weight — an internal efficiency metric.",
    },
    {
      label: "DIFOT Failure Reasons",
      text:  "Reasons are recorded by CSMs in the DIFOT Tool when cleaning remaining consignments after the carrier report phase. Only applies to Tool Cleansed layer — consignments not yet reviewed by a CSM will show as 'No reason recorded'.",
    },
    {
      label: "Filters",
      text:  "All filters apply across every chart and table on this tab. Carrier and State filters apply together. Portfolio filter only affects the Customers tab. Use 'Clear all' to reset.",
    },
  ];

  const OverviewView = () => (
    <div className="space-y-6">
      <AboutBanner items={OVERVIEW_ABOUT} />
      <DifotCards />
      <FinancialCards />

      {/* Weekly trend chart */}
      <Card className="p-6">
        <SectionHeader title="Weekly DIFOT Trend"
          sub={`Three measurement layers week-by-week — dashed line at ${TARGET}% target`} />
        {loadingTrend ? (
          <div className="h-96 flex items-center justify-center text-sm text-gray-400">Loading…</div>
        ) : trendChart.length === 0 ? (
          <div className="h-96 flex items-center justify-center text-sm text-gray-400">No data</div>
        ) : (
          <ResponsiveContainer width="100%" height={500}>
            <AreaChart data={trendChart} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                {[[C_RAW, "gR"], [C_CARRIER, "gC"], [C_TOOL, "gT"]].map(([col, id]) => (
                  <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={col} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={col} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} />
              <YAxis domain={[50, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false} tickLine={false} width={42} />
              <Tooltip content={<Tip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 16 }} />
              <ReferenceLine y={TARGET} stroke={CORAL} strokeDasharray="5 5"
                label={{ value: `${TARGET}% target`, position: "insideTopRight", fontSize: 10, fill: CORAL, dy: -6 }} />
              <Area type="monotone" dataKey="raw_pct"     name="Raw Machship"     stroke={C_RAW}     fill="url(#gR)" strokeWidth={2}   dot={false} activeDot={{ r: 5 }} />
              <Area type="monotone" dataKey="carrier_pct" name="Carrier Cleansed" stroke={C_CARRIER} fill="url(#gC)" strokeWidth={2}   dot={false} activeDot={{ r: 5 }} />
              <Area type="monotone" dataKey="tool_pct"    name="Tool Cleansed"    stroke={C_TOOL}    fill="url(#gT)" strokeWidth={2.5} dot={false} activeDot={{ r: 6 }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* State breakdown */}
      <Card className="p-6">
        <SectionHeader title="DIFOT by Receiving State"
          sub="Tool Cleansed performance by destination state — colour = performance band" />
        {loadingCarrier ? (
          <div className="h-48 flex items-center justify-center text-sm text-gray-400">Loading…</div>
        ) : byState.length === 0 ? (
          <div className="h-32 flex items-center justify-center text-sm text-gray-400">No state data</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(260, byState.length * 42)}>
            <BarChart data={byState} layout="vertical" margin={{ top: 0, right: 70, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
              <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`}
                tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="state" width={48}
                tick={{ fontSize: 12, fill: "#374151", fontWeight: 600 }} axisLine={false} tickLine={false} />
              <Tooltip content={<Tip />} />
              <ReferenceLine x={TARGET} stroke={CORAL} strokeDasharray="5 5" />
              <Bar dataKey="tool_pct" name="Tool Cleansed DIFOT" radius={[0, 4, 4, 0]} barSize={24}
                label={{ position: "right", fontSize: 11, fontWeight: 700, fill: NAVY, formatter: v => v != null ? `${v}%` : "" }}>
                {byState.map((r, i) => (
                  <Cell key={i} fill={pctFill(r.tool_pct)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Failure reasons */}
      {failureReasons && failureReasons.total_misses > 0 && (
        <Card className="p-6">
          <SectionHeader title="Top DIFOT Failure Reasons"
            sub={`${failureReasons.total_misses?.toLocaleString()} missed consignments — reasons recorded by CSMs in the DIFOT Tool (Tool Cleansed layer)`} />
          <ResponsiveContainer width="100%" height={Math.max(220, failureReasons.reasons.length * 42)}>
            <BarChart data={failureReasons.reasons} layout="vertical"
              margin={{ top: 0, right: 90, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
              <XAxis type="number" tickFormatter={v => `${v}%`}
                tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="reason" width={200}
                tick={{ fontSize: 11, fill: "#374151" }} axisLine={false} tickLine={false} />
              <Tooltip
                formatter={(v, n, p) => [`${p.payload.misses?.toLocaleString()} misses (${v}%)`, "Share of misses"]}
                contentStyle={{ borderRadius: 12, border: `1px solid ${NAVY}20`, fontSize: 12 }}
                labelStyle={{ fontWeight: 700, color: NAVY }} />
              <Bar dataKey="pct_of_misses" name="% of misses" radius={[0, 4, 4, 0]} barSize={24}
                label={{ position: "right", fontSize: 11, fontWeight: 700, fill: NAVY,
                  formatter: v => v != null ? `${v}%` : "" }}>
                {failureReasons.reasons.map((r, i) => (
                  <Cell key={i} fill={i === 0 ? CORAL : i === 1 ? BLUE : NAVY} opacity={1 - i * 0.06} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Weekly detail table */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-700">Weekly Detail</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: NAVY }}>
                <TH>Week</TH><TH right>Consignments</TH>
                <TH>Raw</TH><TH>Carrier Cleansed</TH><TH>Tool Cleansed</TH>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loadingTrend
                ? <tr><td colSpan={5} className="px-5 py-6 text-center text-gray-400">Loading…</td></tr>
                : trend.map(r => (
                  <tr key={r.week} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium tabular-nums text-gray-700">{r.week}</td>
                    <td className="px-5 py-3 text-right text-gray-500 tabular-nums">{r.total?.toLocaleString()}</td>
                    <td className="px-5 py-3"><MiniBar value={r.raw_pct} /></td>
                    <td className="px-5 py-3"><MiniBar value={r.carrier_pct} /></td>
                    <td className="px-5 py-3"><MiniBar value={r.tool_pct} /></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     VIEW 2 — CARRIERS
  ══════════════════════════════════════════════════════════ */
  const CARRIER_ABOUT = [
    {
      label: "Drill-Down",
      text:  "Click any carrier row or bar to see a breakdown by that carrier's individual service codes (e.g. Express, Road, Same Day). Use the back arrow to return to the full carrier list.",
    },
    {
      label: "Carrier Cleansed vs Tool Cleansed",
      text:  "Approximately 1 month post-despatch, carriers submit amendment report files covering misses they consider acceptable within their own business rules (weather events, customer-caused delays, etc.). These files are dropped in automatically — they update the Carrier Cleansed figure and surface what still needs CSM attention. CSMs then log into the DIFOT Tool to clean the remaining consignments, making it the Tool Cleansed (official) DIFOT number.",
    },
    {
      label: "Cost per KG",
      text:  "Total Cost Price ÷ Total Weight (KG) for the selected period. Useful for comparing freight efficiency across carriers. This is an internal cost metric — not visible to customers.",
    },
  ];

  const CarrierView = () => (
    <div className="space-y-6">
      <AboutBanner items={CARRIER_ABOUT} />
      <DifotCards />

      {drillCarrier ? (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => setDrillCarrier(null)}
              className="text-xs font-semibold hover:underline flex items-center gap-1"
              style={{ color: BLUE }}>
              ← All Carriers
            </button>
            <span className="text-gray-300">|</span>
            <h2 className="text-base font-bold text-gray-800">{drillCarrier}</h2>
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: `${BLUE}15`, color: BLUE, border: `1px solid ${BLUE}30` }}>
              Service breakdown
            </span>
          </div>

          {loadingDrill ? (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
          ) : drillData.length === 0 ? (
            <div className="h-32 flex items-center justify-center text-gray-400 text-sm">No service data found</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={Math.max(320, drillData.length * 44)}>
                <BarChart data={drillData} layout="vertical" margin={{ top: 0, right: 40, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                  <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`}
                    tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="service" width={160}
                    tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<Tip />} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                  <ReferenceLine x={TARGET} stroke={CORAL} strokeDasharray="5 5" />
                  {[
                    { key: "raw_pct",     name: "Raw",              color: C_RAW },
                    { key: "carrier_pct", name: "Carrier Cleansed", color: C_CARRIER },
                    { key: "tool_pct",    name: "Tool Cleansed",    color: C_TOOL },
                  ].map(({ key, name, color }) => (
                    <Bar key={key} dataKey={key} name={name} fill={color} radius={[0, 3, 3, 0]} barSize={8} />
                  ))}
                </BarChart>
              </ResponsiveContainer>

              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: NAVY }}>
                      <TH>Service</TH><TH right>Consignments</TH>
                      <TH>Raw</TH><TH>Carrier Cleansed</TH><TH>Tool Cleansed</TH>
                      <TH right>Revenue</TH><TH right>GP</TH><TH right>Margin</TH><TH right>Cost/KG</TH>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {drillData.map(r => (
                      <tr key={r.service} className="hover:bg-gray-50">
                        <td className="px-5 py-3 font-medium text-gray-800">{r.service}</td>
                        <td className="px-5 py-3 text-right text-gray-500 tabular-nums">{r.total?.toLocaleString()}</td>
                        <td className="px-5 py-3"><MiniBar value={r.raw_pct} /></td>
                        <td className="px-5 py-3"><MiniBar value={r.carrier_pct} /></td>
                        <td className="px-5 py-3"><MiniBar value={r.tool_pct} /></td>
                        <td className="px-5 py-3 text-right text-gray-600 tabular-nums">{fmtMoney(r.revenue)}</td>
                        <td className="px-5 py-3 text-right text-gray-600 tabular-nums">{fmtMoney(r.gp)}</td>
                        <td className="px-5 py-3 text-right tabular-nums font-semibold"
                          style={{ color: gpColor(r.gp_margin) }}>
                          {r.gp_margin != null ? `${r.gp_margin}%` : "—"}
                        </td>
                        <td className="px-5 py-3 text-right text-gray-500 tabular-nums">{fmtCpkg(r.cost_per_kg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      ) : (
        <>
          <Card className="p-6">
            <SectionHeader title="DIFOT by Carrier"
              sub="Top 15 by volume — click any row or bar to drill into that carrier's service breakdown" />
            {loadingCarrier ? (
              <div className="h-96 flex items-center justify-center text-sm text-gray-400">Loading…</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(400, carrierChart.length * 36)}>
                <BarChart data={carrierChart} layout="vertical" margin={{ top: 0, right: 40, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                  <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`}
                    tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="carrier" width={150}
                    tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<Tip />} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                  <ReferenceLine x={TARGET} stroke={CORAL} strokeDasharray="5 5" />
                  {[
                    { key: "raw_pct",     name: "Raw",              color: C_RAW },
                    { key: "carrier_pct", name: "Carrier Cleansed", color: C_CARRIER },
                    { key: "tool_pct",    name: "Tool Cleansed",    color: C_TOOL },
                  ].map(({ key, name, color }) => (
                    <Bar key={key} dataKey={key} name={name} fill={color} radius={[0, 3, 3, 0]} barSize={8}
                      onClick={r => setDrillCarrier(r.carrier)}>
                      {carrierChart.map((r, i) => (
                        <Cell key={i} opacity={carrier && r.carrier !== carrier ? 0.25 : 1} cursor="pointer" />
                      ))}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card className="overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-700">All Carriers</h3>
              {carrier && (
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                  style={{ background: `${BLUE}15`, color: BLUE, border: `1px solid ${BLUE}30` }}>
                  {carrier} <button onClick={() => setCarrier("")} className="ml-1 font-bold">×</button>
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: NAVY }}>
                    <TH>Carrier</TH><TH right>Consignments</TH>
                    <TH>Raw</TH><TH>Carrier Cleansed</TH><TH>Tool Cleansed</TH>
                    <TH right>Revenue</TH><TH right>GP</TH><TH right>Margin</TH><TH right>Cost/KG</TH>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loadingCarrier
                    ? <tr><td colSpan={9} className="px-5 py-6 text-center text-gray-400">Loading…</td></tr>
                    : byCarrier.map(r => (
                      <tr key={r.carrier} className="hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => setDrillCarrier(r.carrier)}>
                        <td className="px-5 py-3 font-medium text-gray-800 flex items-center gap-2">
                          {r.carrier}
                          <span className="text-xs" style={{ color: BLUE }}>↗</span>
                        </td>
                        <td className="px-5 py-3 text-right text-gray-500 tabular-nums">{r.total?.toLocaleString()}</td>
                        <td className="px-5 py-3"><MiniBar value={r.raw_pct} /></td>
                        <td className="px-5 py-3"><MiniBar value={r.carrier_pct} /></td>
                        <td className="px-5 py-3"><MiniBar value={r.tool_pct} /></td>
                        <td className="px-5 py-3 text-right text-gray-600 tabular-nums">{fmtMoney(r.revenue)}</td>
                        <td className="px-5 py-3 text-right text-gray-600 tabular-nums">{fmtMoney(r.gp)}</td>
                        <td className="px-5 py-3 text-right tabular-nums font-semibold"
                          style={{ color: gpColor(r.gp_margin) }}>
                          {r.gp_margin != null ? `${r.gp_margin}%` : "—"}
                        </td>
                        <td className="px-5 py-3 text-right text-gray-500 tabular-nums">{fmtCpkg(r.cost_per_kg)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
          <StateCarrierMatrix />
        </>
      )}
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     VIEW 3 — CUSTOMERS
  ══════════════════════════════════════════════════════════ */
  const CUSTOMER_ABOUT = [
    {
      label: "Portfolio",
      text:  "Portfolio = Sales Portfolio / CSM assignment from the customer master (DIM_CUSTOMERS). Each customer belongs to one CSM's book. Click a portfolio name in the table or the chart bar to filter the view to that CSM's customers.",
    },
    {
      label: "Customer DIFOT",
      text:  "Calculated across all consignments for that customer in the selected date range, across all carriers. The three layers (Raw, Carrier Cleansed, Tool Cleansed) apply the same way as in the Overview tab.",
    },
    {
      label: "Data Limit",
      text:  "The customer table shows the top 200 customers by consignment volume. If a customer you're looking for isn't listed, they have low volume in the selected period — try widening the date range.",
    },
  ];

  const CustomerView = () => (
    <div className="space-y-6">
      <AboutBanner items={CUSTOMER_ABOUT} />
      <DifotCards />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <SectionHeader title="DIFOT by Portfolio"
            sub="Tool Cleansed % per CSM — colour = performance band. Click to filter." />
          {loadingCustomer ? (
            <div className="h-80 flex items-center justify-center text-sm text-gray-400">Loading…</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(340, portfolioChart.length * 44)}>
              <BarChart data={portfolioChart} layout="vertical" margin={{ top: 0, right: 40, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`}
                  tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="portfolio" width={130}
                  tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                <Tooltip content={<Tip />} />
                <ReferenceLine x={TARGET} stroke={CORAL} strokeDasharray="5 5" />
                <Bar dataKey="tool_pct" name="Tool Cleansed" radius={[0, 4, 4, 0]} barSize={20}
                  onClick={r => setPortfolio(r.portfolio === portfolio ? "" : r.portfolio)}>
                  {portfolioChart.map((r, i) => (
                    <Cell key={i} fill={pctFill(r.tool_pct)}
                      opacity={portfolio && r.portfolio !== portfolio ? 0.25 : 1} cursor="pointer" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-6">
          <SectionHeader title="Top 15 Customers by Volume"
            sub="Tool Cleansed DIFOT % — colour = performance band" />
          {loadingCustomer ? (
            <div className="h-80 flex items-center justify-center text-sm text-gray-400">Loading…</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(340, topCustomerChart.length * 44)}>
              <BarChart data={topCustomerChart} layout="vertical" margin={{ top: 0, right: 40, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`}
                  tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={148}
                  tick={{ fontSize: 10, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                <Tooltip content={<Tip />} />
                <ReferenceLine x={TARGET} stroke={CORAL} strokeDasharray="5 5" />
                <Bar dataKey="tool_pct" name="Tool Cleansed" radius={[0, 4, 4, 0]} barSize={20}>
                  {topCustomerChart.map((r, i) => <Cell key={i} fill={pctFill(r.tool_pct)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-700">All Customers — top 200 by volume</h3>
          {portfolio && (
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: `${BLUE}15`, color: BLUE, border: `1px solid ${BLUE}30` }}>
              Portfolio: {portfolio}
              <button onClick={() => setPortfolio("")} className="ml-1 font-bold">×</button>
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: NAVY }}>
                <TH>Customer</TH><TH>Portfolio</TH><TH right>Consignments</TH>
                <TH>Raw</TH><TH>Carrier Cleansed</TH><TH>Tool Cleansed</TH>
                <TH right>Revenue</TH><TH right>GP</TH><TH right>Margin</TH><TH right>Cost/KG</TH>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loadingCustomer
                ? <tr><td colSpan={10} className="px-5 py-6 text-center text-gray-400">Loading…</td></tr>
                : byCustomer.length === 0
                  ? <tr><td colSpan={10} className="px-5 py-6 text-center text-gray-400">No data</td></tr>
                  : byCustomer.map((r, i) => (
                    <tr key={`${r.customer}-${i}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3 font-medium text-gray-800">{r.customer}</td>
                      <td className="px-5 py-3">
                        <span className="text-xs font-semibold cursor-pointer hover:underline"
                          style={{ color: BLUE }}
                          onClick={() => setPortfolio(r.portfolio === portfolio ? "" : r.portfolio)}>
                          {r.portfolio}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right text-gray-500 tabular-nums">{r.total?.toLocaleString()}</td>
                      <td className="px-5 py-3"><MiniBar value={r.raw_pct} /></td>
                      <td className="px-5 py-3"><MiniBar value={r.carrier_pct} /></td>
                      <td className="px-5 py-3"><MiniBar value={r.tool_pct} /></td>
                      <td className="px-5 py-3 text-right text-gray-600 tabular-nums">{fmtMoney(r.revenue)}</td>
                      <td className="px-5 py-3 text-right text-gray-600 tabular-nums">{fmtMoney(r.gp)}</td>
                      <td className="px-5 py-3 text-right tabular-nums font-semibold"
                        style={{ color: gpColor(r.gp_margin) }}>
                        {r.gp_margin != null ? `${r.gp_margin}%` : "—"}
                      </td>
                      <td className="px-5 py-3 text-right text-gray-500 tabular-nums">{fmtCpkg(r.cost_per_kg)}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     VIEW 4 — REPORTS (MBR / QBR)
  ══════════════════════════════════════════════════════════ */
  const REPORTS_ABOUT = [
    {
      label: "Preset Periods",
      text:  "Monthly Review = 1st of the current month to today. Quarterly Review = start of the current calendar quarter to today. FY to Date = July 1 to today. You can also set a custom range with the date pickers above.",
    },
    {
      label: "Customer Filter",
      text:  "Selecting a customer filters all sections on this tab to that customer only. The dropdown is populated from the top 200 customers by volume in the selected date range. Changing dates may change the available customers.",
    },
    {
      label: "Late Delivery",
      text:  "Based on Tool Cleansed ETA vs actual delivery scan date. Only consignments with both an ETA and a delivery scan are included — consignments still in transit are excluded from late counts.",
    },
    {
      label: "Top Lanes",
      text:  "Ranked by consignment volume. Lane = FROM suburb/state → TO suburb/state. Cost/KG is the internal cost efficiency metric for that lane.",
    },
  ];

  const ReportsView = () => {
    const f  = reportSummary?.financials;
    const d  = reportSummary;
    const la = lateAnalysis;

    return (
      <div className="space-y-6">
        <AboutBanner items={REPORTS_ABOUT} />

        {/* Preset date buttons */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Quick select:</span>
          {[
            { label: "Monthly Review",   preset: "mtd" },
            { label: "Quarterly Review", preset: "qtd" },
            { label: "FY to Date",       preset: "fy"  },
          ].map(({ label, preset }) => (
            <button key={preset} onClick={() => applyPreset(preset)}
              className="px-4 py-2 rounded-xl text-sm font-semibold border transition-all"
              style={{ borderColor: CORAL, color: CORAL, background: "white" }}
              onMouseEnter={e => { e.currentTarget.style.background = CORAL; e.currentTarget.style.color = "white"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "white"; e.currentTarget.style.color = CORAL; }}>
              {label}
            </button>
          ))}
        </div>

        {/* Customer selector */}
        <Card className="p-4 flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Customer</label>
            <select value={reportCustomer} onChange={e => setReportCustomer(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none min-w-[280px]">
              <option value="">All Customers</option>
              {customerList.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {reportCustomer && (
            <button onClick={() => setReportCustomer("")}
              className="self-end pb-2 text-xs font-semibold hover:underline" style={{ color: CORAL }}>
              Clear customer
            </button>
          )}
          {reportSummary && (
            <p className="self-end pb-2 ml-auto text-xs text-gray-400">
              {reportSummary.total?.toLocaleString()} consignments &middot; {fromDate} → {toDate}
              {reportCustomer && <span style={{ color: BLUE }}> &middot; {reportCustomer}</span>}
            </p>
          )}
        </Card>

        {loadingReport ? (
          <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Loading report…</div>
        ) : (
          <>
            {/* Consignment summary KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <KpiCard label="Total Consignments" value={d?.total?.toLocaleString() ?? "—"}
                sub="In selected period" />
              <KpiCard label="Delivered" value={la?.delivered?.toLocaleString() ?? "—"}
                sub={la?.total ? `${la.total > 0 ? ((la.delivered / la.total) * 100).toFixed(1) : 0}% delivery scan rate` : ""} />
              <KpiCard label="POD Availability" pct={d?.pod?.pct}
                value={d?.pod?.pct != null ? `${d.pod.pct}%` : "—"}
                sub={`${d?.pod?.hit?.toLocaleString() ?? "—"} with attachment`} />
              <KpiCard label="Late Consignments" pct={la?.pct_late != null ? (100 - la.pct_late) : null}
                value={la?.total_late != null ? `${la.pct_late}%` : "—"}
                sub={`${la?.total_late?.toLocaleString() ?? "—"} consignments late`} />
            </div>

            {/* DIFOT headline + state grid side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* DIFOT headline */}
              <Card className="p-6 flex flex-col justify-center items-center text-center gap-3">
                <SectionHeader title="DIFOT Performance" sub="Tool Cleansed — cora's official figure" />
                <div className="text-7xl font-black"
                  style={{ color: pctColor(d?.tool?.pct) }}>
                  {d?.tool?.pct != null ? `${d.tool.pct}%` : "—"}
                </div>
                <p className="text-sm text-gray-500">
                  {d?.tool?.hit?.toLocaleString() ?? "—"} on time from {d?.total?.toLocaleString() ?? "—"} consignments
                </p>
                <div className="flex gap-4 text-xs mt-1">
                  <span style={{ color: pctColor(d?.raw?.pct) }}>Raw {d?.raw?.pct ?? "—"}%</span>
                  <span className="text-gray-300">|</span>
                  <span style={{ color: pctColor(d?.carrier?.pct) }}>Carrier {d?.carrier?.pct ?? "—"}%</span>
                  <span className="text-gray-300">|</span>
                  <span style={{ color: pctColor(d?.tool?.pct) }} className="font-bold">Tool {d?.tool?.pct ?? "—"}%</span>
                </div>
              </Card>

              {/* State breakdown */}
              <Card className="p-6">
                <SectionHeader title="By Receiving State" sub="Tool Cleansed DIFOT %" />
                {reportByState.length === 0 ? (
                  <div className="h-48 flex items-center justify-center text-sm text-gray-400">No state data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(220, reportByState.length * 38)}>
                    <BarChart data={reportByState} layout="vertical" margin={{ top: 0, right: 60, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                      <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`}
                        tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="state" width={42}
                        tick={{ fontSize: 12, fill: "#374151", fontWeight: 600 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<Tip />} />
                      <ReferenceLine x={TARGET} stroke={CORAL} strokeDasharray="5 5" />
                      <Bar dataKey="tool_pct" name="Tool Cleansed" radius={[0, 4, 4, 0]} barSize={22}
                        label={{ position: "right", fontSize: 11, fontWeight: 700, fill: NAVY,
                          formatter: v => v != null ? `${v}%` : "" }}>
                        {reportByState.map((r, i) => <Cell key={i} fill={pctFill(r.tool_pct)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </div>

            {/* Late delivery analysis */}
            {la && (() => {
              const scanCoverage = la.total > 0 ? la.delivered / la.total : 0;
              const lowCoverage  = scanCoverage < 0.05;
              return (
                <Card className="p-6">
                  <SectionHeader title="Late Delivery Analysis"
                    sub="ETA vs delivery scan date — only consignments with a delivery scan are included" />
                  {lowCoverage ? (
                    <div className="rounded-xl px-4 py-4 text-sm"
                      style={{ background: `${NAVY}08`, border: `1px solid ${NAVY}20` }}>
                      <p className="font-semibold text-gray-700 mb-1">Delivery scan data not yet available</p>
                      <p className="text-xs text-gray-500">
                        Only {la.delivered?.toLocaleString()} of {la.total?.toLocaleString()} consignments have a
                        delivery scan recorded ({(scanCoverage * 100).toFixed(1)}% coverage).
                        This metric requires the actual delivery timestamp from Machship —
                        raise with the data team to confirm the correct column in FCT_CONSIGNMENTS.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {la.breakdown.map(b => (
                        <div key={b.label} className="rounded-xl border p-4 text-center"
                          style={{ borderColor: `${CORAL}30`, background: `${CORAL}06` }}>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{b.label}</p>
                          <p className="text-3xl font-black" style={{ color: b.count > 0 ? CORAL : RAG_TEXT_G }}>
                            {b.pct_of_total != null ? `${b.pct_of_total}%` : "—"}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">{b.count?.toLocaleString()} consignments</p>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })()}

            {/* Financial summary */}
            <Card className="p-6">
              <SectionHeader title="Financial Summary" sub="Revenue, Cost, GP and efficiency metrics" />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <FinCard label="Revenue"      value={fmtMoney(f?.revenue)} sub="Total sell price" />
                <FinCard label="Cost"         value={fmtMoney(f?.cost)}    sub="Total cost price" />
                <FinCard label="Gross Profit" value={fmtMoney(f?.gp)}      sub="Revenue minus cost" />
                <FinCard label="GP Margin"    value={f?.gp_margin != null ? `${f.gp_margin}%` : "—"}
                  sub="GP as % of revenue" alert={f?.gp_margin != null && f.gp_margin < 8} />
                <FinCard label="Avg Cost/KG"  value={fmtCpkg(f?.cost_per_kg)}
                  sub={la?.total ? `Avg $${f?.cost != null && la.total ? (f.cost / la.total).toFixed(0) : "—"}/consignment` : "Cost efficiency"} />
              </div>
            </Card>

            {/* Top lanes */}
            <Card className="overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-700">Top Lanes by Volume</h3>
                <p className="text-xs text-gray-400 mt-0.5">FROM → TO suburb/state — top 15 lanes in selected period</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: NAVY }}>
                      <TH>Lane</TH><TH right>Consignments</TH>
                      <TH>DIFOT</TH><TH right>Revenue</TH><TH right>Cost</TH>
                      <TH right>Margin</TH><TH right>Cost/KG</TH>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {laneAnalysis.length === 0
                      ? <tr><td colSpan={7} className="px-5 py-6 text-center text-gray-400">No lane data</td></tr>
                      : laneAnalysis.map((r, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-5 py-3 font-medium text-gray-800 text-xs">{r.lane}</td>
                          <td className="px-5 py-3 text-right text-gray-500 tabular-nums">{r.total?.toLocaleString()}</td>
                          <td className="px-5 py-3"><MiniBar value={r.tool_pct} /></td>
                          <td className="px-5 py-3 text-right text-gray-600 tabular-nums">{fmtMoney(r.revenue)}</td>
                          <td className="px-5 py-3 text-right text-gray-600 tabular-nums">{fmtMoney(r.cost)}</td>
                          <td className="px-5 py-3 text-right tabular-nums font-semibold"
                            style={{ color: gpColor(r.gp_margin) }}>
                            {r.gp_margin != null ? `${r.gp_margin}%` : "—"}
                          </td>
                          <td className="px-5 py-3 text-right text-gray-500 tabular-nums">{fmtCpkg(r.cost_per_kg)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════
     VIEW 5 — CROSS-DIMENSIONAL (State × Carrier matrix)
     Rendered as a section at the bottom of the Carriers tab
  ══════════════════════════════════════════════════════════ */
  const StateCarrierMatrix = () => {
    if (loadingMatrix) return (
      <Card className="p-6">
        <SectionHeader title="State × Carrier Matrix" sub="Tool Cleansed DIFOT % — loading…" />
        <div className="h-40 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
      </Card>
    );
    if (!stateCarrier || stateCarrier.matrix.length === 0) return null;
    const { carriers, states, matrix } = stateCarrier;
    return (
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-700">State × Carrier Matrix</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Tool Cleansed DIFOT % per carrier per receiving state — colour = performance band
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: NAVY }}>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider min-w-[160px]">Carrier</th>
                {states.map(s => (
                  <th key={s} className="px-3 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider min-w-[64px]">{s}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {matrix.map(row => (
                <tr key={row.carrier} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-700 text-xs">{row.carrier}</td>
                  {states.map(s => {
                    const v = row.values[s];
                    return (
                      <td key={s} className="px-3 py-2.5 text-center tabular-nums">
                        {v != null ? (
                          <span className="inline-block px-2 py-0.5 rounded-md text-xs font-bold"
                            style={{ background: `${pctFill(v)}22`, color: pctColor(v) }}>
                            {v}%
                          </span>
                        ) : (
                          <span className="text-gray-200">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  /* ─── root render ─────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-[#f0f5fb] p-6">
      <div className="max-w-screen-xl mx-auto space-y-5">

        {/* Page header with coral brand accent */}
        <div className="pl-4" style={{ borderLeft: `4px solid ${CORAL}` }}>
          <Title>DIFOT Intelligence</Title>
          <p className="text-sm text-gray-500 mt-0.5">
            Delivered In Full, On Time — three measurement layers across carriers and customers
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          <ViewTab label="Overview"  active={view === "overview"}  onClick={() => setView("overview")} />
          <ViewTab label="Carriers"  active={view === "carrier"}   onClick={() => { setView("carrier"); setDrillCarrier(null); }} />
          <ViewTab label="Customers" active={view === "customer"}  onClick={() => setView("customer")} />
          <ViewTab label="Reports"   active={view === "reports"}   onClick={() => setView("reports")} />
        </div>

        <FilterBar />

        {error && (
          <div className="rounded-xl px-4 py-3 text-sm font-medium"
            style={{ background: `${CORAL}15`, border: `1px solid ${CORAL}40`, color: CORAL }}>
            {error}
          </div>
        )}

        {view === "overview"  && <OverviewView />}
        {view === "carrier"   && <CarrierView />}
        {view === "customer"  && <CustomerView />}
        {view === "reports"   && <ReportsView />}
      </div>
    </div>
  );
}
